'use client';

import { PropsWithChildren, createContext, useContext, useMemo } from 'react';
import LoadsSearchQuery from '../_lib/loads-search-query';
import { LoadsSearchRequestV2 } from '@trucksmarter/shared';
import {
  createLoadBoardURLSearchParamsFromQuery,
  createLoadsSearchRequestFromQuery,
} from '../_lib/utils';
import { useBrokers } from '../../../../../../_ui/shared/BrokersProvider/BrokersProvider';
import { encodeJsonToBase64 } from '../_lib/client/utils';

const LoadsSearchQueryContext =
  createContext<Readonly<LoadsSearchQuery> | null>(null);

export function useLoadsSearchQuery() {
  const query = useContext(LoadsSearchQueryContext);

  if (query === null) {
    throw new Error(
      'useLoadsSearchQuery must be used within a LoadsSearchRequestProvider',
    );
  }

  return query;
}

const LoadsSearchQueryStringContext = createContext<Readonly<string>>('');

export function useLoadsSearchQueryString() {
  return useContext(LoadsSearchQueryStringContext);
}

const LoadsSearchRequestContext =
  createContext<Readonly<LoadsSearchRequestV2> | null>(null);

export function useLoadsSearchRequest() {
  const searchRequest = useContext(LoadsSearchRequestContext);

  if (searchRequest === null) {
    throw new Error(
      'useLoadsSearchRequest must be used within a LoadsSearchRequestProvider',
    );
  }

  return searchRequest;
}

interface Props {
  query: LoadsSearchQuery;
}

export default function LoadsSearchRequestProvider({
  query,
  children,
}: PropsWithChildren<Props>) {
  const brokers = useBrokers();
  const queryString = useMemo(
    () =>
      createLoadBoardURLSearchParamsFromQuery(
        query,
        brokers,
        encodeJsonToBase64,
      ).toString(),
    [query, brokers],
  );
  const searchRequest = useMemo(
    () => createLoadsSearchRequestFromQuery(query),
    [query],
  );

  return (
    <LoadsSearchQueryContext.Provider value={query}>
      <LoadsSearchQueryStringContext.Provider value={queryString}>
        <LoadsSearchRequestContext.Provider value={searchRequest}>
          {children}
        </LoadsSearchRequestContext.Provider>
      </LoadsSearchQueryStringContext.Provider>
    </LoadsSearchQueryContext.Provider>
  );
}
